import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Image as ImageIcon, Camera, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { listAssets, listFiles, listScreenshots, screenshotUrl } from "@/api/constructor";
import type { ScreenshotRecord } from "@/api/constructor";
import { cn, formatRelativeTime } from "@/lib/utils";

type TabKey = "files" | "assets" | "screenshots";

interface Props {
    draftId: number;
    open: boolean;
    onClose: () => void;
    onOpenFile?: (path: string) => void;
    onOpenScreenshot?: (s: { id: string; url: string; label?: string; capturedAt?: string }) => void;
}

/**
 * The catch-all resource browser for a draft. Lists every file, asset, and
 * screenshot the agent has produced (or the operator uploaded) — chronological
 * where possible — with click-to-open. Mirrors the "marks in the chat" idea
 * but as a flat panel you can scan when the conversation gets long.
 */
export function BrowseResourcesDialog({ draftId, open, onClose, onOpenFile, onOpenScreenshot }: Props): React.ReactNode {
    const [tab, setTab] = React.useState<TabKey>("files");
    const filesQ = useQuery({
        queryKey: ["draft-files", draftId],
        queryFn: () => listFiles(draftId),
        enabled: open,
    });
    const assetsQ = useQuery({
        queryKey: ["draft-assets", draftId],
        queryFn: () => listAssets(draftId),
        enabled: open,
    });
    const shotsQ = useQuery<ScreenshotRecord[]>({
        queryKey: ["draft-screenshots", draftId],
        queryFn: () => listScreenshots(draftId),
        enabled: open && tab === "screenshots",
    });

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>Browse resources</DialogTitle>
                    <DialogDescription>
                        Everything tied to this draft. Files + assets are part of the bundle;
                        screenshots are agent-captured snapshots kept indefinitely for review.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex border-b text-xs">
                    <TabButton active={tab === "files"} onClick={() => setTab("files")}
                        icon={<FileText className="h-3 w-3" />}>
                        Files {filesQ.data && `(${filesQ.data.length})`}
                    </TabButton>
                    <TabButton active={tab === "assets"} onClick={() => setTab("assets")}
                        icon={<ImageIcon className="h-3 w-3" />}>
                        Assets {assetsQ.data && `(${assetsQ.data.length})`}
                    </TabButton>
                    <TabButton active={tab === "screenshots"} onClick={() => setTab("screenshots")}
                        icon={<Camera className="h-3 w-3" />}>
                        Screenshots {shotsQ.data && `(${shotsQ.data.length})`}
                    </TabButton>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                    {tab === "files" && (
                        <FilesList
                            loading={filesQ.isLoading}
                            files={filesQ.data || []}
                            onOpen={(p) => { onOpenFile && onOpenFile(p); onClose(); }}
                        />
                    )}
                    {tab === "assets" && (
                        <AssetsList
                            loading={assetsQ.isLoading}
                            assets={assetsQ.data || []}
                            onOpen={(p) => { onOpenFile && onOpenFile(p); onClose(); }}
                        />
                    )}
                    {tab === "screenshots" && (
                        <ScreenshotsGrid
                            loading={shotsQ.isLoading}
                            shots={shotsQ.data || []}
                            draftId={draftId}
                            onOpen={(s) => { onOpenScreenshot && onOpenScreenshot(s); }}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TabButton({ active, onClick, icon, children }: {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    children: React.ReactNode;
}): React.ReactNode {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex items-center gap-1.5 px-3 py-2 border-b-2 -mb-px",
                active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
        >
            {icon}
            {children}
        </button>
    );
}

interface FileRow { path: string; sizeBytes: number; updatedAt?: string; }

function FilesList({ loading, files, onOpen }: { loading: boolean; files: FileRow[]; onOpen: (p: string) => void }): React.ReactNode {
    if (loading) return <LoadingState />;
    if (files.length === 0) return <EmptyState text="No files yet — the AI hasn't written any." />;
    // Sort by updatedAt descending if present, else by path
    const rows = [...files].sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
    return (
        <ul className="divide-y">
            {rows.map((f) => (
                <li key={f.path} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-muted/40">
                    <button
                        onClick={() => onOpen(f.path)}
                        className="flex-1 text-left font-mono hover:underline"
                        data-testid={`browse-file-${f.path}`}
                    >
                        {f.path}
                    </button>
                    <span className="text-muted-foreground tabular-nums w-16 text-right">{formatBytes(f.sizeBytes)}</span>
                    {f.updatedAt && (
                        <span className="text-muted-foreground w-24 text-right">{formatRelativeTime(f.updatedAt)}</span>
                    )}
                </li>
            ))}
        </ul>
    );
}

interface AssetRow { path: string; sizeBytes: number; mime?: string; createdAt?: string; }

function AssetsList({ loading, assets, onOpen }: { loading: boolean; assets: AssetRow[]; onOpen: (p: string) => void }): React.ReactNode {
    if (loading) return <LoadingState />;
    if (assets.length === 0) return <EmptyState text="No assets yet — generated images and forked files land here." />;
    const rows = [...assets].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    return (
        <ul className="divide-y">
            {rows.map((a) => (
                <li key={a.path} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-muted/40">
                    <button
                        onClick={() => onOpen(a.path)}
                        className="flex-1 text-left font-mono hover:underline"
                        data-testid={`browse-asset-${a.path}`}
                    >
                        {a.path}
                    </button>
                    <span className="text-muted-foreground w-20 text-right">{a.mime || ""}</span>
                    <span className="text-muted-foreground tabular-nums w-16 text-right">{formatBytes(a.sizeBytes)}</span>
                    {a.createdAt && (
                        <span className="text-muted-foreground w-24 text-right">{formatRelativeTime(a.createdAt)}</span>
                    )}
                </li>
            ))}
        </ul>
    );
}

function ScreenshotsGrid({ loading, shots, draftId, onOpen }: {
    loading: boolean;
    shots: ScreenshotRecord[];
    draftId: number;
    onOpen: (s: { id: string; url: string; capturedAt?: string }) => void;
}): React.ReactNode {
    if (loading) return <LoadingState />;
    if (shots.length === 0) return <EmptyState text="No screenshots yet — browse_url / render_page / screenshot_preview captures land here." />;
    return (
        <div className="grid grid-cols-3 gap-2 p-3">
            {shots.map((s) => (
                <button
                    key={s.id}
                    onClick={() => onOpen({ id: s.id, url: screenshotUrl(draftId, s.id), capturedAt: s.capturedAt })}
                    className="group block overflow-hidden rounded-md border bg-card hover:border-primary"
                    data-testid={`browse-shot-${s.id}`}
                >
                    <div className="aspect-video bg-muted">
                        <img
                            src={screenshotUrl(draftId, s.id)}
                            alt={s.id}
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    </div>
                    <div className="flex items-center justify-between px-2 py-1 text-[10px]">
                        <span className="font-mono text-muted-foreground truncate">{s.id.slice(-12)}</span>
                        {s.capturedAt && (
                            <span className="text-muted-foreground">{formatRelativeTime(s.capturedAt)}</span>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
}

function EmptyState({ text }: { text: string }): React.ReactNode {
    return <div className="px-3 py-12 text-center text-xs text-muted-foreground">{text}</div>;
}

function LoadingState(): React.ReactNode {
    return (
        <div className="flex items-center justify-center px-3 py-12 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
        </div>
    );
}

function formatBytes(n: number | undefined): string {
    if (!n) return "—";
    if (n < 1024) return `${n}b`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}k`;
    return `${(n / 1024 / 1024).toFixed(2)}M`;
}
